import sortBy from 'lodash-es/sortBy';
import React, { useMemo } from 'react';
import { FeatureFlagName } from 'shared/enums/FeatureFlagName';
import { allWildcards, WildcardValues } from 'shared/enums/WildcardOptions';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { Menu, MenuItemProps } from '@televet/kibble-ui/build/components/Menu';
import { ButtonSize } from '@televet/kibble-ui';

export interface WildCardButtonProps {
  availableWildCards: WildcardValues[];
  inputRef: React.MutableRefObject<HTMLTextAreaElement | HTMLInputElement | null>;
  onInputValueChange?: (value: string) => void;
  setIsInputFocused: React.Dispatch<React.SetStateAction<boolean>>;
  setIsMenuFocused: React.Dispatch<React.SetStateAction<boolean>>;
  size?: ButtonSize;
}

const WildCardButton = ({
  availableWildCards,
  inputRef,
  onInputValueChange,
  setIsInputFocused,
  setIsMenuFocused,
  size = 'sm',
}: WildCardButtonProps): JSX.Element => {
  const { isFeatureEnabled } = useFeatureFlag();
  const hasCareEnabled = useMemo(() => {
    return isFeatureEnabled(FeatureFlagName.CarePlans);
  }, [isFeatureEnabled]);

  const allowedWildCardOptions = useMemo(() => {
    const newArray: MenuItemProps[] = allWildcards.filter((wildcard) => {
      if (!hasCareEnabled && wildcard.value === WildcardValues.PetPortalLinksForPets) {
        return;
      } else if (availableWildCards.includes(wildcard.value)) {
        return wildcard;
      }
    });

    return sortBy(newArray, (e) => e.label);
  }, [hasCareEnabled, availableWildCards]);

  const insertTextInBetween = (textBody: string, textInsert: string, start: number, end: number): string => {
    const result = textBody.slice(0, start) + textInsert + textBody.slice(end);
    return result;
  };

  const handleOnMenuOpen = (): void => {
    setIsInputFocused(false);
    setIsMenuFocused(true);
  };

  const handleOnMenuClose = (): void => {
    setIsMenuFocused(false);
  };

  const onSelectWildCard = (option: MenuItemProps): void => {
    if (!inputRef || !option.value) return;

    // TODO: Fix menu item component to "unselect" menu items that are not select based
    option.isSelected = false;

    const currentValue = inputRef?.current?.value || '';
    const selectionStart = inputRef?.current?.selectionStart || 0;
    const selectionEnd = inputRef?.current?.selectionEnd || 0;

    const newValue = insertTextInBetween(currentValue, option.value, selectionStart, selectionEnd);
    if (inputRef.current) {
      inputRef.current.value = newValue;
      inputRef.current.selectionStart = selectionStart + option.value.length;
      inputRef.current.selectionEnd = selectionStart + option.value.length;
    }
    if (onInputValueChange) {
      onInputValueChange(newValue);
    }
    setIsInputFocused(true);
    setIsMenuFocused(false);
    inputRef.current?.focus();
  };

  return (
    <Menu
      buttonProps={{
        variant: 'tertiary',
        text: 'Insert Tag',
        size,
      }}
      listProps={{
        menuItems: allowedWildCardOptions,
        onSelect: onSelectWildCard,
        shouldRetainSelection: false,
      }}
      containerProps={{
        onOpen: handleOnMenuOpen,
        onClose: handleOnMenuClose,
      }}
    />
  );
};

export default WildCardButton;
